import React, { useEffect, useState, useMemo, useRef } from "react";
import { Nav, NavItem } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import "./Index.scss";
import { useSelector } from "react-redux";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import SchoolRounded from "@mui/icons-material/SchoolRounded";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import SpaRounded from "@mui/icons-material/SpaRounded";
import RouteRounded from "@mui/icons-material/RouteRounded";
import UploadFileRounded from "@mui/icons-material/UploadFileRounded";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import Diversity3Rounded from "@mui/icons-material/Diversity3Rounded";
import HandshakeRounded from "@mui/icons-material/HandshakeRounded";
import InsightsRounded from "@mui/icons-material/InsightsRounded";
import AssessmentRounded from "@mui/icons-material/AssessmentRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import CloudUploadRounded from "@mui/icons-material/CloudUploadRounded";
import MenuBookOutlined from "@mui/icons-material/MenuBookOutlined";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import RestaurantRounded from "@mui/icons-material/RestaurantRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";

const ICON_MAP = {
  DashboardRounded,
  SchoolRounded,
  MenuBookRounded,
  SpaRounded,
  RouteRounded,
  UploadFileRounded,
  Groups2Rounded,
  ForumRounded,
  Diversity3Rounded,
  HandshakeRounded,
  InsightsRounded,
  AssessmentRounded,
  SettingsRounded,
  CloudUploadRounded,
  MenuBookOutlined,
  StorefrontRounded,
  RestaurantRounded,
  LinkRounded,
};

// Fallback config when database config isn't loaded yet
// Menu URLs to hide from the top bar
const HIDDEN_MENU_URLS = ["/fig-agent", "/figagent"];

const FALLBACK_CONFIG = [
  { groupName: null, groupIcon: "DashboardRounded", groupSequence: 1, menuUrl: "/dashboard", menuIcon: "DashboardRounded", itemSequence: 1 },
  { groupName: null, groupIcon: "InsightsRounded", groupSequence: 1, menuUrl: "/state-dashboard", menuIcon: "InsightsRounded", itemSequence: 2, position: "right" },
  { groupName: "Training", groupIcon: "SchoolRounded", groupSequence: 2, menuUrl: "/ceu-courses", menuIcon: "MenuBookRounded", itemSequence: 1 },
  { groupName: "Training", groupIcon: "SchoolRounded", groupSequence: 2, menuUrl: "/fig-lifestyle", menuIcon: "SpaRounded", itemSequence: 2 },
  { groupName: "Training", groupIcon: "SchoolRounded", groupSequence: 2, menuUrl: "/learning-paths", menuIcon: "RouteRounded", itemSequence: 3 },
  { groupName: null, groupIcon: "Diversity3Rounded", groupSequence: 3, menuUrl: "/fig-family", menuIcon: "Diversity3Rounded", itemSequence: 1 },
  { groupName: "Preferred Partners", groupIcon: "HandshakeRounded", groupSequence: 4, menuUrl: "/preferred-partners", menuIcon: "HandshakeRounded", itemSequence: 1 },
  { groupName: "Preferred Partners", groupIcon: "HandshakeRounded", groupSequence: 4, menuUrl: "/recipe", menuIcon: "RestaurantRounded", itemSequence: 3 },
  { groupName: null, groupIcon: "MenuBookOutlined", groupSequence: 5, menuUrl: "/free-sources", menuIcon: "MenuBookOutlined", itemSequence: 1 },
  { groupName: "Connect", groupIcon: "Groups2Rounded", groupSequence: 6, menuUrl: "/community", menuIcon: "ForumRounded", itemSequence: 1 },
  { groupName: "Connect", groupIcon: "Groups2Rounded", groupSequence: 6, menuUrl: "https://www.facebook.com/groups/schoolnutritionindustryprofessionals", menuIcon: "Groups2Rounded", itemSequence: 2, labelOverride: "SNIP" },
  { groupName: "Connect", groupIcon: "Groups2Rounded", groupSequence: 6, menuUrl: "/community-groups", menuIcon: "Groups2Rounded", itemSequence: 3 },
  { groupName: null, groupIcon: "AssessmentRounded", groupSequence: 8, menuUrl: "/reports", menuIcon: "AssessmentRounded", itemSequence: 1 },
  { groupName: "Settings", groupIcon: "SettingsRounded", groupSequence: 9, menuUrl: "/settings", menuIcon: "SettingsRounded", itemSequence: 1 },
  { groupName: "Settings", groupIcon: "SettingsRounded", groupSequence: 9, menuUrl: "/training-intake", menuIcon: "CloudUploadRounded", itemSequence: 2, labelOverride: "AI Import" },
  { groupName: "Settings", groupIcon: "SettingsRounded", groupSequence: 9, menuUrl: "/allocation-groups", menuIcon: "HandshakeRounded", itemSequence: 3, labelOverride: "Portfolio Accounts" },
  { groupName: null, groupIcon: "StorefrontRounded", groupSequence: 10, menuUrl: "https://market.figk-12.com/", menuIcon: "StorefrontRounded", itemSequence: 1 },
];

const ICON_COLORS = {
  DashboardRounded: "#1ebec8",
  SchoolRounded: "#6c5ce7",
  MenuBookRounded: "#1ebec8",
  SpaRounded: "#86c65a",
  RouteRounded: "#f79153",
  UploadFileRounded: "#36454f",
  Groups2Rounded: "#86c65a",
  ForumRounded: "#1ebec8",
  Diversity3Rounded: "#f79153",
  HandshakeRounded: "#6c5ce7",
  InsightsRounded: "#1ebec8",
  AssessmentRounded: "#f79153",
  SettingsRounded: "#86c65a",
  CloudUploadRounded: "#1ebec8",
  MenuBookOutlined: "#6c5ce7",
  StorefrontRounded: "#e17055",
  RestaurantRounded: "#fa4747",
};

const getIcon = (name) => {
  const Icon = ICON_MAP[name];
  const color = ICON_COLORS[name] || "#7f8c8d";
  return Icon ? <Icon sx={{ fontSize: 18, color }} /> : <LinkRounded sx={{ fontSize: 18, color: "#7f8c8d" }} />;
};

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

function NavigateComponent(props) {
  const nav = useNavigate();
  const location = useLocation();
  const [pathname, setPathname] = useState("");
  const TopMenuList = useSelector((state) => state.setting?.TopMenuList);
  const roleId = useSelector((state) => state.login?.LoginDetails?.roleId);

  useEffect(() => {
    setPathname(location?.pathname);
  }, [location]);

  const isHidden = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return HIDDEN_MENU_URLS.some((h) => lower.includes(h)) || lower.includes("fig agent") || lower.includes("figagent");
  };

  // Build the set of accessible menuUrls from TopMenuList (include old items for grouping)
  const accessibleUrls = useMemo(() => {
    const urls = new Set();
    const list = TopMenuList || [];
    for (const m of list) {
      if (m.menuUrl && !isHidden(m.menuUrl) && !isHidden(m.menuName)) urls.add(m.menuUrl);
    }
    if (roleId === 1) urls.add("/training-intake");
    if (roleId === 1) urls.add("/allocation-groups");
    if (roleId === 1 || roleId === 14) urls.add("/state-dashboard");
    return urls;
  }, [TopMenuList, roleId]);

  // Map menuUrl -> menu item (for getting menuName, include old items)
  const menuByUrl = useMemo(() => {
    const map = {};
    const list = TopMenuList || [];
    for (const m of list) {
      if (m.menuUrl && !isHidden(m.menuUrl) && !isHidden(m.menuName)) map[m.menuUrl] = m;
    }
    if (roleId === 1) map["/training-intake"] = { menuName: "Training Intake", menuUrl: "/training-intake" };
    if (roleId === 1) map["/allocation-groups"] = { menuName: "Portfolio Accounts", menuUrl: "/allocation-groups" };
    if (roleId === 1 || roleId === 14) map["/state-dashboard"] = { menuName: "Portfolio Management", menuUrl: "/state-dashboard" };
    return map;
  }, [TopMenuList, roleId]);

  // Always use the in-code FALLBACK_CONFIG as the source of truth so the top
  // bar is deterministic. The DB NavGroupConfig table lacks columns
  // (labelOverride, position) needed to express the full layout, and mixing
  // sources caused the bar to mutate after login as Redux hydrated.
  // navGroupConfig is intentionally ignored.
  const config = FALLBACK_CONFIG;

  // Build grouped nav structure
  const navGroups = useMemo(() => {
    const groups = [];
    const groupMap = {};

    // Sort config by groupSequence, then itemSequence
    const sorted = [...config].sort((a, b) => (a.groupSequence || 0) - (b.groupSequence || 0) || (a.itemSequence || 0) - (b.itemSequence || 0));

    for (const item of sorted) {
      // Skip items the user doesn't have access to
      if (!accessibleUrls.has(item.menuUrl)) continue;

      const menuItem = menuByUrl[item.menuUrl];
      if (!menuItem) continue;

      if (!item.groupName) {
        // Standalone item
        groups.push({
          type: "single",
          label: item.labelOverride || menuItem.menuName,
          menuUrl: item.menuUrl,
          icon: item.menuIcon || item.groupIcon,
          sequence: item.groupSequence,
          isExternal: isExternalURL(item.menuUrl),
          position: item.position || "left",
        });
      } else {
        // Grouped item
        if (!groupMap[item.groupName]) {
          groupMap[item.groupName] = {
            type: "group",
            label: item.groupName,
            icon: item.groupIcon,
            sequence: item.groupSequence,
            children: [],
          };
          groups.push(groupMap[item.groupName]);
        }
        groupMap[item.groupName].children.push({
          label: item.labelOverride || menuItem.menuName,
          menuUrl: item.menuUrl,
          icon: item.menuIcon,
          isExternal: isExternalURL(item.menuUrl),
        });
      }
    }

    return groups;
  }, [config, accessibleUrls, menuByUrl]);

  const handleNav = (menuUrl) => {
    props?.closeSideNav();
    nav(menuUrl, { replace: true });
  };

  const isActive = (menuUrl) => pathname?.split("/")[1] === menuUrl?.split("/")[1];

  const isGroupActive = (children) => children?.some((c) => isActive(c.menuUrl));

  const leftGroups = navGroups.filter((g) => g.position !== "right");
  const rightGroups = navGroups.filter((g) => g.position === "right");

  const renderGroup = (group, i) =>
        group.type === "single" ? (
          <NavItem className="header_active" key={group.menuUrl || i}>
            {group.isExternal ? (
              <a href={group.menuUrl} target="_blank" className="header_link" rel="noopener noreferrer">
                {getIcon(group.icon)}
                <span>{group.label}</span>
              </a>
            ) : (
              <div
                onClick={() => handleNav(group.menuUrl)}
                className={`header_link ${isActive(group.menuUrl) ? "active" : ""}`}
              >
                {getIcon(group.icon)}
                <span>{group.label}</span>
              </div>
            )}
          </NavItem>
        ) : (
          <NavGroupDropdown
            key={group.label}
            group={group}
            isGroupActive={isGroupActive(group.children)}
            onNavigate={handleNav}
            isActive={isActive}
          />
        );

  return (
    <Nav
      navbar
      className="header_item"
      style={{ display: "flex", listStyle: "none", alignItems: "center", width: "100%" }}
    >
      {leftGroups.map(renderGroup)}
      {rightGroups.length > 0 && <div style={{ marginLeft: "auto" }} />}
      {rightGroups.map(renderGroup)}
    </Nav>
  );
}

function NavGroupDropdown({ group, isGroupActive, onNavigate, isActive }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <NavItem
      className="header_active nav-group"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div className={`header_link nav-group__trigger ${isGroupActive ? "active" : ""}`}>
        {getIcon(group.icon)}
        <span>{group.label}</span>
        <KeyboardArrowDownRounded
          className="nav-group__chevron"
          sx={{ fontSize: 16, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}
        />
      </div>
      {open && (
        <div className="nav-group__dropdown">
          {group.children.map((child) => (
            <div
              key={child.menuUrl}
              className={`nav-group__item ${isActive(child.menuUrl) ? "active" : ""}`}
              onClick={() => {
                onNavigate(child.menuUrl);
                setOpen(false);
              }}
            >
              {getIcon(child.icon)}
              <span>{child.label}</span>
            </div>
          ))}
        </div>
      )}
    </NavItem>
  );
}

export default React.memo(NavigateComponent);
